
import Link from '@/components/compat/Link';
import Image from '@/components/compat/Image';
import { ArrowRight, Calendar, Infinity, Loader2, Lock, Store } from 'lucide-react';
import {
  type PowerMentionData,
  formatPowerDisplayName,
  getPowerMentionSmileyCode,
} from '@/lib/power-mention-data';
import {
  formatFairtradePrice,
  formatReleaseDate,
  formatStorePrice,
  getPowerCategoryLabel,
} from '@/lib/powers-data';
import { getPowerCategoryBadgeStyle } from '@/lib/power-category-theme';
import { GsImage, XatsPriceValue, cn } from '@/components/help-center/latest-power-shared';
import { POWER_FAIRTRADE_ICON_SRC } from '@/components/help-center/powers/power-shared';

function AvailabilityBadge({ limited }: { limited: boolean }) {
  if (limited) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-widest text-white"
        style={{
          background: 'linear-gradient(90deg, #e8620a 0%, #f47c1a 50%, #e8a020 100%)',
          boxShadow: '0 0 0 1.5px rgba(240,130,30,0.40), 0 4px 14px rgba(220,90,10,0.35)',
        }}
      >
        <Lock className="h-3 w-3" aria-hidden />
        Limitado
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-cyan-500/30 to-sky-500/30 px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-widest text-cyan-300 ring-1 ring-cyan-400/35">
      <Infinity className="h-3 w-3" aria-hidden />
      Ilimitado
    </span>
  );
}

function PreviewRow({
  icon,
  iconSrc,
  label,
  value,
}: {
  icon?: React.ElementType;
  iconSrc?: string;
  label: string;
  value: React.ReactNode;
}) {
  const Icon = icon;
  if (!value) return null;

  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="flex min-w-0 items-center gap-2 text-blue-100/55">
        {iconSrc ? (
          <Image src={iconSrc} alt="" width={14} height={14} className="h-3.5 w-3.5 shrink-0 opacity-90" />
        ) : Icon ? (
          <Icon className="h-3.5 w-3.5 shrink-0 text-sky-400/80" aria-hidden />
        ) : null}
        <span className="truncate">{label}</span>
      </span>
      <span className="shrink-0 text-right font-medium text-blue-50">{value}</span>
    </div>
  );
}

interface PowerMentionPreviewProps {
  data: PowerMentionData | null;
  loading?: boolean;
  variant?: 'popover' | 'sheet';
  onNavigate?: () => void;
}

export default function PowerMentionPreview({
  data,
  loading = false,
  variant = 'popover',
  onNavigate,
}: PowerMentionPreviewProps) {
  const categoryStyle = data ? getPowerCategoryBadgeStyle(data.category) : null;
  const displayName = data ? formatPowerDisplayName(data.name) : '';
  const shortDesc = data?.description.short?.trim() || data?.description.long?.trim() || '';
  const storePrice = data ? formatStorePrice(data.prices.store) : '';
  const fairtradePrice = data
    ? formatFairtradePrice(data.prices.fairtrade_min, data.prices.fairtrade_max)
    : '';
  const releaseDate = data?.released ? formatReleaseDate(data.released) : '';
  const releaseValue = releaseDate && releaseDate !== '—' ? releaseDate : '—';

  const showStore = Boolean(data && storePrice && storePrice !== '—');
  const showFairtrade = Boolean(
    data && fairtradePrice && fairtradePrice !== '—' && (data.prices.fairtrade_min || data.prices.fairtrade_max)
  );
  const showRelease = Boolean(data);
  const showAvailability = Boolean(data);
  const showDivider =
    showRelease || showStore || showFairtrade || showAvailability;

  return (
    <div
      className={cn(
        'flex flex-col text-blue-50',
        variant === 'popover' ? 'p-4' : 'px-1 pb-2 pt-1'
      )}
    >
      {loading ? (
        <div className="flex min-h-[120px] items-center justify-center gap-2 text-sm text-blue-100/60">
          <Loader2 className="h-4 w-4 animate-spin text-sky-400" aria-hidden />
          A carregar…
        </div>
      ) : !data ? (
        <p className="py-6 text-center text-sm text-blue-100/60">Power não encontrado.</p>
      ) : (
        <>
          <div className="flex gap-3">
            <div
              className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-2xl"
              style={{
                background:
                  'radial-gradient(circle at 50% 48%, #112268 0%, #0a1450 42%, #060c38 75%, #030719 100%)',
                boxShadow:
                  '0 0 0 1.5px #0b1658, 0 0 0 2.5px #0f1e70, 0 0 20px rgba(56,189,248,0.12)',
              }}
            >
              <GsImage
                code={getPowerMentionSmileyCode(data)}
                className="flex h-full w-full items-center justify-center"
                imgClassName="max-h-[56px] max-w-[56px] object-contain"
              />
            </div>

            <div className="min-w-0 flex-1 pt-0.5">
              <span
                className="inline-flex items-center rounded-full border px-2.5 py-0.5 font-poppins text-[10px] font-bold uppercase tracking-wide"
                style={categoryStyle ?? undefined}
              >
                {getPowerCategoryLabel(data.category)}
              </span>

              <h3 className="mt-2 font-poppins text-base font-bold leading-tight text-white">
                {displayName}
              </h3>

              {shortDesc ? (
                <p className="mt-1.5 line-clamp-3 font-inter text-[13px] leading-relaxed text-blue-50/90">
                  {shortDesc}
                </p>
              ) : null}
            </div>
          </div>

          {showDivider ? (
            <>
              <div className="my-3 h-px bg-gradient-to-r from-transparent via-sky-400/25 to-transparent" />
              <div className="space-y-2">
                {showRelease ? (
                  <PreviewRow
                    icon={Calendar}
                    label="Lançamento"
                    value={
                      <span className="font-poppins text-xs font-semibold text-blue-50 sm:text-sm">
                        {releaseValue}
                      </span>
                    }
                  />
                ) : null}
                {showStore ? (
                  <PreviewRow
                    icon={Store}
                    label="Preço na Store"
                    value={<XatsPriceValue>{storePrice}</XatsPriceValue>}
                  />
                ) : null}
                {showFairtrade ? (
                  <PreviewRow
                    iconSrc={POWER_FAIRTRADE_ICON_SRC}
                    label="Preço Fairtrade"
                    value={<XatsPriceValue>{fairtradePrice}</XatsPriceValue>}
                  />
                ) : null}
                {showAvailability ? (
                  <PreviewRow
                    icon={data.limited ? Lock : Infinity}
                    label="Disponibilidade"
                    value={<AvailabilityBadge limited={data.limited} />}
                  />
                ) : null}
              </div>
            </>
          ) : null}

          <Link
            href={`/powers/${data.slug}`}
            onClick={onNavigate}
            className={cn(
              'mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5',
              'font-poppins text-xs font-bold uppercase tracking-wider text-white',
              'bg-gradient-to-r from-sky-600 via-sky-500 to-cyan-400',
              'shadow-[0_0_0_1px_rgba(56,189,248,0.35),0_4px_16px_rgba(14,165,233,0.25)]',
              'transition-[filter,transform] duration-200 hover:brightness-110 active:scale-[0.98]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#030023]'
            )}
          >
            Ver página
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </>
      )}
    </div>
  );
}
